// EDV Next 0.11.0 - FMEA workspace.
//
// Uses the EXISTING typed fmeaApi + shared calculations for RPN. Visual
// language and interactions are ported from the reviewed FMEA prototype:
//   * 10-step green→red S/O/D badges (strong palette)
//   * Bucketed RPN badges (low <100, med 100-199, high ≥200)
//   * Full-text S/O/D scale tooltip on hover (Sev/Occ/Det)
//   * KPI stat strip (studies, failure modes, high-risk, open actions,
//     critical-open badge, closed)
//   * Actions Register with status pills + hide-closed filter
// NO localStorage, NO second sync engine, NO independent DB write path — this
// component uses api.js / fmea-api.js which are wired through the shared
// SyncEngine outbox.
import React, { useEffect, useMemo, useState } from 'react';
import {
  fmeaApi,
  type FmeaStudy,
  type FailureMode,
  type ActionStatus,
  type FmeaHistoryEntry,
} from './fmea-api.js';
import { useSession } from './session.js';
import { useSync } from './sync.js';
import {
  sodBadgeStyle,
  rpn,
  rpnBucket,
  rpnBucketStyle,
  ACTION_STATUS_META,
  SEV_SCALE,
  OCC_SCALE,
  DET_SCALE,
} from './fmea-ui.js';

type Tab = 'studies' | 'fmea' | 'actions' | 'history';

/** Colored S/O/D picker (1-10) with full-scale tooltip on hover. */
function SodPicker({
  axis, value, onChange, label,
}: {
  axis: 'S'|'O'|'D';
  value: number | null | undefined;
  onChange(v: number | null): void;
  label: string;
}) {
  const s = sodBadgeStyle(value);
  const scale = axis === 'S' ? SEV_SCALE : axis === 'O' ? OCC_SCALE : DET_SCALE;
  return (
    <span
      className="sod-wrap"
      title={`${axis}=${value ?? '—'} (${scale[value ?? 0] || 'not set'})`}
      style={{ position: 'relative', display: 'inline-block' }}
      data-testid={`sod-${axis.toLowerCase()}`}
    >
      <select
        aria-label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        style={{
          position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%',
        }}
      >
        <option value="">—</option>
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>{n} — {scale[n]}</option>
        ))}
      </select>
      <span
        aria-hidden
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 30, height: 30, borderRadius: 4, fontWeight: 700,
          background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
          borderStyle: s.empty ? 'dashed' : 'solid',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value ?? '—'}
      </span>
    </span>
  );
}

/** Bucketed RPN badge. */
function RpnBadge({ s, o, d, revised }: { s: number|null; o: number|null; d: number|null; revised?: boolean }) {
  const r = rpn(s, o, d);
  const b = rpnBucket(r);
  const st = rpnBucketStyle(b);
  return (
    <span
      data-testid={`rpn-${revised ? 'new' : 'init'}`}
      title={r === null ? 'RPN unavailable' : `${revised ? "S'" : 'S'} ${s} × ${revised ? "O'" : 'O'} ${o} × ${revised ? "D'" : 'D'} ${d}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 44, padding: '2px 6px', borderRadius: 3, fontWeight: 700,
        background: st.bg, color: st.fg, border: `1px solid ${st.border}`,
        borderStyle: b === 'none' ? 'dashed' : 'solid',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {r === null ? '—' : String(r)}
    </span>
  );
}

function ActionStatusPill({ status }: { status: ActionStatus }) {
  const m = ACTION_STATUS_META[status];
  return (
    <span
      data-testid={`action-status-${status}`}
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 999,
        background: m.bg, color: m.fg, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
      }}
    >{m.label}</span>
  );
}

function StatStrip({ modes, actions, criticality }: {
  modes: FailureMode[];
  actions: FailureMode[];
  criticality?: number;
}) {
  const total = modes.length;
  const rated = modes.filter(m => m.s !== null && m.o !== null && m.d !== null).length;
  const highRisk = modes.filter(m => {
    const r = rpn(m.s, m.o, m.d);
    return r !== null && r >= 200;
  }).length;
  const topRpn = modes.reduce((max, m) => Math.max(max, rpn(m.s, m.o, m.d) ?? 0), 0);
  const open = actions.filter(a => a.action_status !== 'completed' && a.action_status !== 'cancelled').length;
  const closed = actions.length - open;
  const critical = criticality === 1;
  return (
    <div
      role="group"
      aria-label="FMEA KPIs"
      data-testid="fmea-stat-strip"
      style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid #e0e6ed' }}
    >
      <Kpi label={`${total} failure modes`} />
      <Kpi label={`${rated} / ${total} rated`} />
      <Kpi label={`${highRisk} high-risk (RPN ≥ 200)`} {...(highRisk > 0 ? { accent: '#c62828' } : {})} />
      <Kpi label={`Top RPN ${topRpn || '—'}`} />
      <Kpi
        label={`${open} open action(s)`}
        {...(critical && open > 0 ? { accent: '#c62828', badge: 'CRITICAL' } : {})}
      />
      <Kpi label={`${closed} closed`} accent="#2e7d32" />
    </div>
  );
}
function Kpi({ label, accent, badge }: { label: string; accent?: string; badge?: string }) {
  return (
    <span style={{
      padding: '4px 10px', borderRadius: 4, background: '#f0f4f8', color: accent ?? '#0d2b45',
      fontSize: 12, fontWeight: 600, display: 'inline-flex', gap: 8, alignItems: 'center',
    }}>
      {label}
      {badge && (
        <span style={{ background: '#c62828', color: '#fff', padding: '1px 6px', borderRadius: 3, fontSize: 10 }}>{badge}</span>
      )}
    </span>
  );
}

// ---- Main workspace ----
export function FmeaWorkspace({ siteId }: { siteId: string }) {
  const sess = useSession();
  useSync();  // asserts a single sync provider is present at the shell level
  const [tab, setTab] = useState<Tab>('studies');
  const [studies, setStudies] = useState<FmeaStudy[] | null>(null);
  const [openStudy, setOpenStudy] = useState<FmeaStudy | null>(null);
  const [modes, setModes] = useState<FailureMode[] | null>(null);
  const [history, setHistory] = useState<FmeaHistoryEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function reloadStudies() {
    try {
      const r = await fmeaApi.listStudies(siteId);
      setStudies(r.data);
    } catch (e) { setErr((e as Error).message); }
  }
  async function openStudyAndLoad(s: FmeaStudy) {
    setOpenStudy(s);
    try {
      const r = await fmeaApi.listFailureModes(s.id);
      setModes(r.data);
    } catch (e) { setErr((e as Error).message); }
  }
  useEffect(() => {
    setOpenStudy(null); setModes(null); setHistory(null);
    void reloadStudies();
  }, [siteId]);
  useEffect(() => {
    if (tab !== 'history' || !openStudy) return;
    (async () => {
      try {
        const r = await fmeaApi.history(openStudy.id);
        setHistory(r.data);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, [tab, openStudy]);

  const modeCount   = openStudy ? (modes?.length ?? 0) : 0;
  const actionCount = openStudy ? (modes ?? []).filter(m =>
    (m.recommended_action?.trim()?.length ?? 0) > 0
  ).length : 0;
  const openActions = openStudy ? (modes ?? []).filter(m =>
    (m.recommended_action?.trim()?.length ?? 0) > 0 &&
    m.action_status !== 'completed' && m.action_status !== 'cancelled'
  ).length : 0;

  return (
    <section aria-label="Failure Modes and Effects Analysis">
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ margin: 0 }}>FMEA</h2>
        <nav aria-label="FMEA tabs" role="tablist">
          <TabBtn active={tab === 'studies'} onClick={() => setTab('studies')}>
            Studies <small>({studies?.length ?? '…'})</small>
          </TabBtn>
          <TabBtn active={tab === 'fmea'}    onClick={() => setTab('fmea')} disabled={!openStudy}>
            FMEA <small>({modeCount})</small>
          </TabBtn>
          <TabBtn active={tab === 'actions'} onClick={() => setTab('actions')} disabled={!openStudy}>
            Actions <small>({openActions} open · {actionCount} total)</small>
          </TabBtn>
          <TabBtn active={tab === 'history'} onClick={() => setTab('history')} disabled={!openStudy}>
            History
          </TabBtn>
        </nav>
      </header>
      {err && <p role="alert">{err}</p>}
      {tab === 'studies' && (
        <StudiesTab
          studies={studies}
          onOpen={openStudyAndLoad}
          onCreate={async () => {
            try {
              await fmeaApi.createStudy(siteId, sess.me!.device.id, {
                title: 'New FMEA study',
                methodology_key: 'legacy-prototype',
                methodology_version: 1,
              });
              await reloadStudies();
            } catch (e) { setErr((e as Error).message); }
          }}
        />
      )}
      {tab === 'fmea' && openStudy && (
        <FmeaTableTab
          study={openStudy}
          modes={modes ?? []}
          deviceId={sess.me!.device.id}
          onModes={setModes}
        />
      )}
      {tab === 'actions' && openStudy && (
        <ActionsRegisterTab
          modes={(modes ?? []).filter(m => (m.recommended_action?.trim()?.length ?? 0) > 0)}
          studyId={openStudy.id}
          deviceId={sess.me!.device.id}
          onModes={(next) => {
            const map = new Map(next.map(n => [n.id, n]));
            setModes((cur) => (cur ?? []).map(m => map.get(m.id) ?? m));
          }}
        />
      )}
      {tab === 'history' && openStudy && (
        <HistoryTab history={history} />
      )}
    </section>
  );
}

function TabBtn({ active, onClick, disabled, children }: {
  active: boolean; onClick(): void; disabled?: boolean; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px', border: '1px solid #ccc',
        borderBottom: active ? '2px solid #0b5394' : '1px solid #ccc',
        background: active ? '#eef4fb' : '#fff',
        fontWeight: active ? 600 : 400,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >{children}</button>
  );
}

function StudiesTab({
  studies, onOpen, onCreate,
}: { studies: FmeaStudy[] | null; onOpen(s: FmeaStudy): void; onCreate(): void }) {
  if (!studies) return <div role="status" aria-live="polite">Loading…</div>;
  return (
    <div>
      <div style={{ padding: '8px 0' }}>
        <button type="button" onClick={onCreate} data-testid="fmea-new-study">
          + New study
        </button>
      </div>
      {studies.length === 0 ? (
        <p data-testid="fmea-empty">No FMEA studies yet for this site.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {studies.map((s) => (
            <li key={s.id} style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
              <button type="button" onClick={() => onOpen(s)} style={{ background: 'none', border: 'none', color: '#0b5394', cursor: 'pointer' }}>
                {s.title} — <small>{s.status}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FmeaTableTab({
  study, modes, deviceId, onModes,
}: { study: FmeaStudy; modes: FailureMode[]; deviceId: string; onModes(next: FailureMode[]): void }) {
  const actionsWithText = useMemo(() =>
    modes.filter(m => (m.recommended_action?.trim()?.length ?? 0) > 0), [modes]);
  return (
    <div>
      <StatStrip modes={modes} actions={actionsWithText} />
      {modes.length === 0 ? (
        <p data-testid="fmea-modes-empty">No failure modes in this study yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 30 }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
              {['S','O','D','RPN'].map((_, i) => <col key={'i'+i} style={{ width: 46 }} />)}
              <col style={{ width: '13%' }} />
              <col style={{ width: '18%' }} />
              {["S'","O'","D'","RPN'"].map((_, i) => <col key={'n'+i} style={{ width: 46 }} />)}
            </colgroup>
            <thead>
              <tr style={{ background: '#0d2b45', color: '#fff' }}>
                {['#','Failure Mode','Effect','Cause','S','O','D','RPN','Controls','Recommended Action',"S'","O'","D'","RPN'"].map((h) => (
                  <th key={h} style={{ padding: '6px 8px', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modes.map((m, i) => (
                <FmRow key={m.id} n={i+1} mode={m} studyId={study.id} deviceId={deviceId} onSaved={(next) => onModes(modes.map(x => x.id === m.id ? next : x))} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FmRow({ n, mode, studyId, deviceId, onSaved }: { n: number; mode: FailureMode; studyId: string; deviceId: string; onSaved(m: FailureMode): void }) {
  const [pending, setPending] = useState<Partial<FailureMode>>({});
  const merged = { ...mode, ...pending };
  async function patch(next: Partial<FailureMode>) {
    setPending((p) => ({ ...p, ...next }));
    try {
      const r = await fmeaApi.patchFailureMode(mode.id, studyId, deviceId, mode.version, next as Record<string, unknown>);
      onSaved(r as FailureMode);
      setPending({});
    } catch (e) { console.error((e as Error).message); }
  }
  return (
    <tr>
      <td style={{ textAlign: 'right', color: '#889', fontVariantNumeric: 'tabular-nums' }}>{n}</td>
      <td><input aria-label="failure mode" value={merged.failure_mode ?? ''} onChange={(e) => patch({ failure_mode: e.target.value })} style={{ width: '100%' }} /></td>
      <td><input aria-label="local effect" value={merged.local_effect ?? ''} onChange={(e) => patch({ local_effect: e.target.value })} style={{ width: '100%' }} /></td>
      <td><input aria-label="cause"        value={merged.causes ?? ''}       onChange={(e) => patch({ causes: e.target.value })}       style={{ width: '100%' }} /></td>
      <td><SodPicker axis="S" value={merged.s} onChange={(v) => patch({ s: v })} label="Severity" /></td>
      <td><SodPicker axis="O" value={merged.o} onChange={(v) => patch({ o: v })} label="Occurrence" /></td>
      <td><SodPicker axis="D" value={merged.d} onChange={(v) => patch({ d: v })} label="Detectability" /></td>
      <td><RpnBadge s={merged.s} o={merged.o} d={merged.d} /></td>
      <td><input aria-label="controls"           value={merged.current_controls ?? ''}   onChange={(e) => patch({ current_controls: e.target.value })}   style={{ width: '100%' }} /></td>
      <td><input aria-label="recommended action" value={merged.recommended_action ?? ''} onChange={(e) => patch({ recommended_action: e.target.value })} style={{ width: '100%' }} /></td>
      <td><SodPicker axis="S" value={merged.s_new} onChange={(v) => patch({ s_new: v })} label="Revised Severity" /></td>
      <td><SodPicker axis="O" value={merged.o_new} onChange={(v) => patch({ o_new: v })} label="Revised Occurrence" /></td>
      <td><SodPicker axis="D" value={merged.d_new} onChange={(v) => patch({ d_new: v })} label="Revised Detectability" /></td>
      <td><RpnBadge s={merged.s_new} o={merged.o_new} d={merged.d_new} revised /></td>
    </tr>
  );
}

function ActionsRegisterTab({
  modes, studyId, deviceId, onModes,
}: { modes: FailureMode[]; studyId: string; deviceId: string; onModes(next: FailureMode[]): void }) {
  const [hideClosed, setHideClosed] = useState(true);
  const filtered = modes.filter(m => !hideClosed || (m.action_status !== 'completed' && m.action_status !== 'cancelled'));
  const counts = (['open','in-progress','blocked','completed','cancelled'] as ActionStatus[]).reduce((acc, s) => {
    acc[s] = modes.filter(m => m.action_status === s).length; return acc;
  }, {} as Record<ActionStatus, number>);

  async function patchStatus(m: FailureMode, next: ActionStatus) {
    const r = await fmeaApi.patchFailureMode(m.id, studyId, deviceId, m.version, { action_status: next });
    onModes([r as FailureMode]);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', flexWrap: 'wrap' }}>
        {(Object.keys(counts) as ActionStatus[]).map(s => (
          <span key={s} style={{
            padding: '2px 8px', borderRadius: 999,
            background: ACTION_STATUS_META[s].bg, color: ACTION_STATUS_META[s].fg,
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          }}>{ACTION_STATUS_META[s].label} {counts[s]}</span>
        ))}
        <label style={{ marginLeft: 'auto' }}>
          <input
            type="checkbox"
            checked={hideClosed}
            onChange={(e) => setHideClosed(e.target.checked)}
            data-testid="hide-closed"
          />{' '}Hide closed
        </label>
      </div>
      {modes.length === 0 ? (
        <p data-testid="actions-empty">Every recommended action from the FMEA tab appears here with owner, due date, and status. No actions yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0d2b45', color: '#fff' }}>
              {['FM','Action','Owner','Due','Status'].map(h => (
                <th key={h} style={{ padding: '6px 8px', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id}>
                <td style={{ fontFamily: 'monospace', color: '#0b5394' }}>{m.failure_mode || m.id.slice(0, 8)}</td>
                <td>{m.recommended_action}</td>
                <td>{m.action_owner ?? ''}</td>
                <td>{m.action_due ?? ''}</td>
                <td>
                  <select
                    aria-label={`action status ${m.id}`}
                    value={m.action_status}
                    onChange={(e) => void patchStatus(m, e.target.value as ActionStatus)}
                    style={{
                      background: ACTION_STATUS_META[m.action_status].bg,
                      color:      ACTION_STATUS_META[m.action_status].fg,
                      border: '1px solid ' + ACTION_STATUS_META[m.action_status].fg,
                      borderRadius: 999, padding: '2px 8px', fontWeight: 600, fontSize: 11,
                    }}
                  >
                    {(['open','in-progress','blocked','completed','cancelled'] as ActionStatus[]).map(s => (
                      <option key={s} value={s}>{ACTION_STATUS_META[s].label}</option>
                    ))}
                  </select>
                  <ActionStatusPill status={m.action_status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function HistoryTab({ history }: { history: FmeaHistoryEntry[] | null }) {
  if (!history) return <div role="status" aria-live="polite">Loading history…</div>;
  if (history.length === 0) return <p>No history yet.</p>;
  return (
    <ul style={{ listStyle: 'none', padding: 0 }}>
      {history.map((h) => (
        <li key={h.id} style={{ padding: '4px 0', borderBottom: '1px solid #eee', fontSize: 12 }}>
          <code>{h.at}</code> — <b>{h.action}</b> on <code>{h.entity_type}:{h.entity_id}</code>
        </li>
      ))}
    </ul>
  );
}
